import {createStore, applyMiddleware, compose} from 'redux';
import {createEpicMiddleware} from 'redux-observable';

import ENV from 'web-app/config/environment';
import {type Action} from 'web-app/util/redux/action-types';

import rootEpic from '../root-epic';
import mainReducer, {type RootState} from './main-reducer';

declare const window: {__REDUX_DEVTOOLS_EXTENSION_COMPOSE__: (...args: any[]) => any};

/*
|------------------------------------------------------------------------------
| Epic middleware
|------------------------------------------------------------------------------
*/
const epicMiddleware = createEpicMiddleware<Action<any>, Action<any>, RootState, any>();

/*
|------------------------------------------------------------------------------
| Action logger middleware
|------------------------------------------------------------------------------
|
| This is useful for logging Redux actions in CI if you want to debug flaky
| tests. If you want to log the entire action along with the payload, set the
| `logEntireAction` boolean to `true`.
|
| To use, include it in the `applyMiddleware` call where we create the store:
|
| composeEnhancers(applyMiddleware(epicMiddleware, actionLogger))
|
*/
const logEntireAction = false;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const actionLogger = () => next => (action: Action<any>) => {
    if (logEntireAction) {
        // eslint-disable-next-line no-console
        console.log(action);
    } else {
        // eslint-disable-next-line no-console
        console.log(action.type);
    }

    return next(action);
};

/*
|------------------------------------------------------------------------------
| Setup Redux devtools
|------------------------------------------------------------------------------
*/
const composeEnhancers =
    (ENV.environment === 'development' &&
        window &&
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({trace: false, traceLimit: 25})) || // set trace: true to be able to trace redux action calls: http://extension.remotedev.io/docs/Features/Trace.html
    compose;

// mount it on the Store
const store = createStore<RootState, Action<any>, any, any>(
    mainReducer,
    composeEnhancers(applyMiddleware(epicMiddleware)),
);

epicMiddleware.run(rootEpic);

/* Used to avoid circular reference
 * in files which use store and
 * they are also imported in this file
 */
export const getStore = () => store;

export default store;

// expose store when run in Cypress
if ((window as any).Cypress) {
    (window as any).getStore = () => store;
}
